import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Heading,
  Flex,
  Button,
  Icon,
  Text,
  SimpleGrid,
  Avatar,
  useToast,
  ButtonGroup,
} from '@chakra-ui/react';
import { RiDeleteBinLine, RiEditLine } from 'react-icons/ri';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { DeleteConfirmationModal } from '../../../../../components/DeleteConfirmationModal';
import { LinkButton } from '../../../../../components/LinkButton';
import { maskDate } from '../../../../../utils/formatters/handleMask';
import {
  IDetailedUser,
  showUsersService,
} from '../../../../../services/Users/ShowUsersService';
import { deleteUsersService } from '../../../../../services/Users/DeleteUsersService';

interface IUser extends IDetailedUser {
  formattedDate: string;
}

interface ILocationState {
  userId: string;
}

export const UserDetails = (): JSX.Element => {
  const toast = useToast();
  const {
    state: { userId },
  } = useLocation<ILocationState>();
  const { push } = useHistory();

  const [user, setUser] = useState<IUser>();
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);

  useEffect(() => {
    async function loadUser(): Promise<void> {
      try {
        const userData = await showUsersService(userId);

        const userWithformattedDate = {
          ...userData,
          formattedDate: maskDate(userData.createdAt),
        };

        setUser(userWithformattedDate);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              'Ocorreu um erro ao carregar os detalhes do usuário, tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }

    loadUser();
  }, [userId, toast]);

  const handleToggleDeleteConfirmationModal = useCallback(() => {
    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleDeleteUser = useCallback(async () => {
    try {
      await deleteUsersService(userId);

      toast({
        title: 'Excluído com sucesso',
        description: 'O usuário foi excluído corretamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });

      push('/users');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao excluir',
          description: 'Ocorreu um erro ao excluir o usuário, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    }
  }, [userId, push, toast]);

  return (
    <DefaultLayout>
      <DeleteConfirmationModal
        isOpen={isDeleteConfirmationModalVisible}
        onClose={handleToggleDeleteConfirmationModal}
        onConfirm={handleDeleteUser}
      />

      <Box flex="1" borderRadius={8} bg="white" p="8">
        <Flex mb="8" justify="space-between" align="center">
          <Heading size="lg" fontWeight="normal">
            Detalhes do usuário
          </Heading>

          <ButtonGroup>
            <LinkButton
              colorScheme="yellow"
              color="white"
              icon={<Icon as={RiEditLine} fontSize="16" />}
              to={{
                pathname: '/users/update',
                state: {
                  userId,
                },
              }}
            >
              Editar
            </LinkButton>

            <Button
              size="sm"
              fontSize="sm"
              colorScheme="red"
              onClick={handleToggleDeleteConfirmationModal}
              leftIcon={<Icon as={RiDeleteBinLine} fontSize="20" />}
            >
              Excluir
            </Button>
          </ButtonGroup>
        </Flex>

        {user && (
          <Flex direction="column">
            <Flex align="center" direction="column">
              <Avatar size="2xl" name={user.name} src={user.avatarUrl} />
              <Flex>
                {user.userRoles &&
                  user.userRoles.map((role, index) => (
                    <Text
                      fontSize="lg"
                      display="row"
                      mt="1"
                      mr="1"
                      color="gray.500"
                    >
                      {` ${role.name} `}
                      {index !== user.userRoles.length - 1 && '-'}
                    </Text>
                  ))}
              </Flex>
              <Text fontSize="4xl" fontWeight="bold" mt="4">
                {user.name}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {user.bio}
              </Text>
            </Flex>

            <SimpleGrid
              minChildWidth="240px"
              spacing="8"
              px="8"
              mt="12"
              w="100%"
            >
              <Text>
                Telefone:
                <Text as="span" color="gray.500" ml="2">
                  {user.phone}
                </Text>
              </Text>
              <Text>
                E-mail:
                <Text as="span" color="gray.500" ml="2">
                  {user.email}
                </Text>
              </Text>
              <Text>
                Data de cadastro:
                <Text as="span" color="gray.500" ml="2">
                  {user.formattedDate}
                </Text>
              </Text>
            </SimpleGrid>
          </Flex>
        )}
      </Box>
    </DefaultLayout>
  );
};
